/** Thin fetch wrapper: bearer token, problem+json errors with a stable `code`, 204 handling. */

const TOKEN_KEY = 'compass.token'

export class ApiError extends Error {
  status: number
  code: string
  /** For VALIDATION_FAILED: plain-language message per field (a question's key, or name, mobile, dateOfBirth, guardian.name ...). */
  fields: Record<string, string>
  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message)
    this.status = status
    this.code = code
    this.fields = fields
  }
  /** True when retrying later could succeed (no connection, server trouble, throttling). */
  get transient(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500
  }
}

let token: string | null = readStored()

function readStored(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function getToken(): string | null {
  return token
}

export function setToken(value: string | null): void {
  token = value
  try {
    if (value) sessionStorage.setItem(TOKEN_KEY, value)
    else sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    // Private mode: the token stays in memory for this tab only.
  }
}

/**
 * Names this browser tab to the server so only one window at a time can run a test (FR-C21). It survives a reload of the same tab
 * (so a refresh keeps the test) and differs for any other tab or device.
 */
export function sessionId(): string {
  const key = 'compass.session'
  try {
    let id = sessionStorage.getItem(key)
    if (!id) {
      id = randomId()
      sessionStorage.setItem(key, id)
    }
    return id
  } catch {
    return memorySession
  }
}
const memorySession = randomId()

function randomId(): string {
  const bytes = new Uint8Array(12)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export const UNAUTHORIZED_EVENT = 'compass:unauthorized'

interface Options {
  method?: 'GET' | 'POST' | 'PUT'
  body?: unknown
  signal?: AbortSignal
}

export async function api<T>(path: string, { method = 'GET', body, signal }: Options = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  headers['X-Compass-Session'] = sessionId()

  let res: Response
  try {
    res = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), signal })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new ApiError(0, 'NETWORK', 'You appear to be offline')
  }

  if (res.status === 401 && token) {
    setToken(null)
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
  }
  if (res.status === 204) return undefined as T
  const text = await res.text()
  const data = text ? safeJson(text) : undefined
  if (!res.ok) {
    const problem = (data ?? {}) as { code?: string; detail?: string; title?: string; fields?: Record<string, string> }
    throw new ApiError(res.status, problem.code ?? `HTTP_${res.status}`, problem.detail ?? problem.title ?? 'Something went wrong', problem.fields && !Array.isArray(problem.fields) ? problem.fields : {})
  }
  return data as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}
